import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {emitChangedWait, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel} from '@mui/material';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {useTheme} from '@mui/material/styles';
import {obsStyles} from '../obsStyle';

function ObsNeuroEyesOpen(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [eyesOpenLevel, setEyesOpenLevel] = React.useState(props.eyesOpenLevel);
    const [eyesClosedBySwelling, setEyesClosedBySwelling] = React.useState(props.eyesClosedBySwelling);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [eyesOpenLevels, setEyesOpenLevels] = React.useState([]);

    useEffect(() => {
        let eyes = [];
        if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._16_99) {
                eyes = [
                    {
                        id: '4',
                        description: t('Spontaneously'),
                        score: 4,
                        // default: true,
                    },
                    {
                        id: '3',
                        description: t('To Speech'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('To Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._5_16) {
                eyes = [
                    {
                        id: '4',
                        description: t('Spontaneously'),
                        score: 4,
                        // default: true,
                    },
                    {
                        id: '3',
                        description: t('To Speech'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('To Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('Never'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._1_5) {
                eyes = [
                    {
                        id: '4',
                        description: t('Spontaneous'),
                        score: 4,
                        // default: true,
                    },
                    {
                        id: '3',
                        description: t('To Sound'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('To Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            }
        }
        setEyesOpenLevels(eyes);
    }, [type]);

    function setDefaults() {
        let def = _.find(eyesOpenLevels, (item) => !!item.default);
        // if (def) {
        setEyesOpenLevel(def);
        // }
    }

    useEffect(() => {
        if (!eyesClosedBySwelling && !eyesOpenLevel) {
            setDefaults();
        }
    }, [eyesOpenLevels, eyesClosedBySwelling]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
        setEyesClosedBySwelling(false);
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (eyesOpenLevel && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [eyesClosedBySwelling, eyesOpenLevel, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    eyesOpenLevel,
                    eyesClosedBySwelling,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        eyesOpenLevel,
        eyesClosedBySwelling,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = undefined;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (eyesOpenLevel) {
                newScore = eyesOpenLevel.score;
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, eyesOpenLevel, eyesClosedBySwelling]);

    const onLevelChange = (value) => {
        setEyesOpenLevel(value);
        setEyesClosedBySwelling(false);
        clearAccepted();
    };

    const onEyesClosedChange = (value) => {
        setEyesClosedBySwelling(value);
        if (eyesClosedBySwelling) {
            setDefaults();
        } else {
            setEyesOpenLevel({
                id: '0',
                description: t('C'),
                score: undefined,
            });
        }
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    let scoreColor = 'neuro';
    if (score >= 4) {
        scoreColor = 'neuroLow';
    } else if (score === 3) {
        scoreColor = 'neuroMedium';
    } else if (score >= 1) {
        scoreColor = 'neuroHigh';
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.divRow}>
                        <AgyleBlockSwitch
                            onChange={onEyesClosedChange}
                            value={eyesClosedBySwelling}
                            customLabels={{
                                yes: t('Yes'),
                                no: t('No'),
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                        />

                        <FormLabel className={classes.label} style={{marginLeft: 5}}>
                            {t('Eyes closed by swelling')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Eyes Open Level')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onLevelChange(value)}
                                    options={eyesOpenLevels}
                                    value={eyesOpenLevel}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        score,
                        renderScore: (
                            <div className={clsx([classes.scoreBadge, scoreColor])}>
                                {eyesClosedBySwelling && t('C')}
                                {!eyesClosedBySwelling && _.isUndefined(score) && (
                                    <span>&nbsp;</span>
                                )}
                                {!eyesClosedBySwelling && _.isNumber(score) && score}
                            </div>
                        ),
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: !eyesClosedBySwelling && !eyesOpenLevel,
                        hideStatuses: true,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroEyesOpen.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    eyesOpenLevel: PropTypes.object,
    eyesClosedBySwelling: PropTypes.bool,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsNeuroEyesOpen.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    eyesOpenLevel: null,
    eyesClosedBySwelling: false,
    accepted: false,
    acceptDate: null,
};
// ObsNeuroEyesOpen.renderSectionScore = ({
export const renderObsNeuroEyesOpenSectionScore = ({
                                                       classes,
                                                       t,
                                                       section,
                                                       obs,
                                                       status,
                                                       score,
                                                       accepted,
                                                   }) => {
    let color = 'neuro';
    if (score >= 4) {
        color = 'neuroLow';
    } else if (score === 3) {
        color = 'neuroMedium';
    } else if (score >= 1) {
        color = 'neuroHigh';
    }
    ;

    return ((obs?.eyesClosedBySwelling || score) &&
        <div
            className={clsx([
                classes.sectionScore,
                color,
                accepted && classes.sectionScoreSelected,
            ])}>
            {obs.eyesClosedBySwelling && t('C')}
            {!obs.eyesClosedBySwelling && _.isUndefined(score) && (
                <span>&nbsp;</span>
            )}
            {!obs.eyesClosedBySwelling && _.isNumber(score) && score}
        </div>
    );
};

export default React.memo(ObsNeuroEyesOpen);